/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { inject, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { AppRouter } from '@gh/common-shared';
import { isMessageBasket } from '@gh/core-messages';
import { DialogRouteSnapshot, NotificationService, WaitingMode, WaitingService } from '@gh/core-ui';
import { expandObservable, logWarning } from '@gh/core-util';
import { isFunction, isNil, isString } from 'lodash';
import { Observable } from 'rxjs';
import { ContextRoute, ContextRouteAttachment, ContextRouter } from './context';

function callInitView(service: any, selector: string | ((...args: string[]) => any), ...args: any[]): any {
  if (isFunction(selector)) {
    return selector(service, ...args);
  } else {
    return service[selector](...args);
  }
}

export function initContextViewResolverFn(route: ContextRoute): Observable<any> | Promise<any> | any {
  const injector = inject(Injector);
  const waitingService = inject(WaitingService);

  const initView = route.config.data['initView'];

  if (initView) {
    const { service, selector } = initView.service
      ? initView
      : { service: initView, selector: void 0 };

    const serviceInstance = injector.get(service);
    const ob$ = expandObservable(callInitView(serviceInstance, selector || 'initContextView', route));
    return initView.noWaiting ? ob$ : ob$.pipe(waitingService.operator(WaitingMode.Background));
  } else {
    return void 0;
  }
}

export const contextableInitViewResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> => {
  const contextRouter = inject(ContextRouter);
  const router = inject(AppRouter);
  const injector = inject(Injector);
  const notificationService = inject(NotificationService);

  const { contextRoute } = route.data as any;

  if (isNil(contextRoute)) {
    throw new Error('ContextableInitViewResolver: contextRoute was not defined');
  }

  const childRouter = contextRouter.createChild(injector);
  if (isString(contextRoute)) {
    logWarning('ContextableInitViewResolver: routing to string-based contextRoute is not supported yet');
  } else {
    childRouter.addRoute(contextRoute);
  }
  const outlet = childRouter.createOutlet(ContextRouteAttachment.Router);
  return outlet.navigate(contextRoute.path, { ...route.params, ...route.queryParams })
    .then((success) => {
      if (success) {
        return Promise.resolve({ outlet });
      } else {
        outlet.destroy();
        return Promise.reject('route was forbidden');
      }
    })
    .catch((error) => {
      outlet.destroy();
      if (isMessageBasket(error) && error.has('msg_resource_not_found')) {
        router.navigateByUrl('/404');
        return Promise.reject();
      } else {
        notificationService.processAll(error);
        return Promise.reject(error);
      }
    });
};

export function contextableInitDialogViewResolverFn(route: DialogRouteSnapshot<any>): Promise<any> {
  const contextRouter = inject(ContextRouter);
  const injector = inject(Injector);
  const notificationService = inject(NotificationService);

  const { contextRoute } = route.routeConfig.data;

  if (isNil(contextRoute)) {
    throw new Error('ContextableInitDialogViewResolver: contextRoute was not defined');
  }

  const childRouter = contextRouter.createChild(injector);
  if (isString(contextRoute)) {
    logWarning('ContextableInitDialogViewResolver: routing to string-based contextRoute is not supported yet');
  } else {
    childRouter.addRoute(contextRoute);
  }
  const outlet = childRouter.createOutlet(ContextRouteAttachment.Dialog);
  return outlet.navigate(contextRoute.path, route.input)
    .then((success) => {
      if (success) {
        return Promise.resolve({ context: { outlet } });
      } else {
        outlet.destroy();
        return Promise.reject('route was forbidden');
      }
    })
    .catch((error) => {
      outlet.destroy();
      notificationService.processAll(error);
      return Promise.reject(error);
    });
}
